// ThemeSwitcher.tsx

import React from "react";
import Tooltip from "@mui/material/Tooltip";
import IconButton from "@mui/material/IconButton";
import { AnimatePresence, motion } from "framer-motion";
import { Sun, Moon } from "lucide-react";

interface ThemeSwitcherProps {
  darkMode: boolean;
  toggleDarkMode: () => void;
}

const ThemeSwitcher: React.FC<ThemeSwitcherProps> = ({
  darkMode,
  toggleDarkMode,
}) => {
  return (
    <Tooltip
      title={darkMode ? "Switch to Light Mode" : "Switch to Dark Mode"}
      arrow
    >
      <IconButton
        onClick={toggleDarkMode}
        className="
          relative
          !w-[40px]
          !h-[40px]
          !rounded-sm
          overflow-hidden
          transition-all
          duration-300

          hover:scale-[1.04]
          active:scale-[0.96]
        "
        sx={{
          background: "rgb(var(--surface-3))",
          border: "1px solid rgb(var(--border-gold))",
          backdropFilter: "blur(10px)",
          "&:hover": {
            background: darkMode
              ? `
                linear-gradient(
                  180deg,
                  rgba(36,36,42,1) 0%,
                  rgba(20,20,24,1) 100%
                )
              `
              : `
                linear-gradient(
                  180deg,
                  rgba(255,255,255,1) 0%,
                  rgba(244,244,246,1) 100%
                )
              `,
          },
        }}
      >
        {/* Glow */}
        <div
          className="
            absolute
            inset-0
            opacity-0
            transition-opacity
            duration-300
            group-hover:opacity-100
          "
          style={{
            background: darkMode
              ? `
                radial-gradient(
                  circle,
                  rgba(255,255,255,0.06),
                  transparent 70%
                )
              `
              : `
                radial-gradient(
                  circle,
                  rgba(212,168,74,0.14),
                  transparent 70%
                )
              `,
          }}
        />

        {/* Animated Icon */}
        <AnimatePresence mode="wait">
          {darkMode ? (
            <motion.div
              key="moon"
              initial={{
                rotate: -90,
                opacity: 0,
                scale: 0.7,
              }}
              animate={{
                rotate: 0,
                opacity: 1,
                scale: 1,
              }}
              exit={{
                rotate: 90,
                opacity: 0,
                scale: 0.7,
              }}
              transition={{
                duration: 0.28,
                ease: "easeOut",
              }}
              className="relative z-10"
            >
              <Moon
                size={18}
                strokeWidth={2.2}
                className="text-[#E5E7EB]"
              />
            </motion.div>
          ) : (
            <motion.div
              key="sun"
              initial={{
                rotate: 90,
                opacity: 0,
                scale: 0.7,
              }}
              animate={{
                rotate: 0,
                opacity: 1,
                scale: 1,
              }}
              exit={{
                rotate: -90,
                opacity: 0,
                scale: 0.7,
              }}
              transition={{
                duration: 0.28,
                ease: "easeOut",
              }}
              className="relative z-10"
            >
              <Sun
                size={18}
                strokeWidth={2.2}
                className="text-[#D4A84A]"
              />
            </motion.div>
          )}
        </AnimatePresence>
      </IconButton>
    </Tooltip>
  );
};

export default ThemeSwitcher;